import { useEffect, useRef, useState } from 'react';
import { useFormField, useFormFiles } from '@/form/FormProvider';
import { Button } from '@/form/FormComponents';
import { useModal, Modal } from '@/components/Modal';
import { getStorage, ref as storageRef, uploadBytesResumable } from "firebase/storage";
import { useFunctionalDatabaseSync } from '@/systems/DatabaseSync';
import { Icon } from '@/components/Icon';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FileImage } from '@/components/Image';
import { publicAction } from '@/data/loaders';

export function FilePicker({ title, type, small, multiple = false, gallery = true, onUploadStart, onUploadComplete, onUploadError, children, ...args }) {

  const please_wait_message = "Please wait for all file uploads to complete.";
  const { files } = useFormFiles();

  // formatted value is an array of objects with file_id, and in the future other file instance specific options like a title, etc.
  // parsed value is an array of objects with file_id, status, error, etc.

  const { name, value, updateValue, error, setError, updateError, required, disabled, readOnly, obviated } = useFormField({
    validateValue: ({value, required}) => {
      if (required && value.length === 0) {
        return required;
      }
      if (value.some(file => !file.file && file.status !== "done")) {
        return please_wait_message;
      }
    },
    formatValue: (value, pretty) => (value || []).map(file => {return { file_id: file.file_id }}),
    parseValue: (value) => value != null ? value.map(file => (file.status ? file : { ...file, file: files.find(f => f._id === file.file_id) })) : [],
    getFocusElement: ({name, parent}) => parent.querySelector(`.file_picker_gallery_container[name="${name}"]`),
    ...args
  });

  const is_uploading = value.some(file => (!file.file && file.status !== "done"));

  const { databaseSync } = useFunctionalDatabaseSync();
  const syncOffs = useRef([]);

  const [activeFile, setActiveFile] = useState(null);

  const fileInputRef = useRef(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChooseFile = () => {
    fileInputRef.current.click();
  }

  const handleFileChange = (event) => {
    let files = Array.from(event.target.files);
    if (files.length > 0) {
      if (!multiple) {
        files = [files[0]];
      }
      let randomIds = files.map(() => Math.random().toString(36).substring(2, 15));
      let imageSrcs = files.map(file => {
        if (file.type.startsWith("image/")) {
          return new Promise(function(resolve, reject) {
            let reader = new FileReader();
            reader.onload = (event) => {
              resolve(event.target.result);
            }
            reader.onerror = (event) => {
              reject(event.target.error);
            }
            reader.readAsDataURL(file);
          })
        } else {
          return Promise.resolve()
        }
      })
      Promise.all(imageSrcs).then(function(srcs) {
        updateValue(draft => {
          if (!multiple) {
            draft.splice(0, draft.length);
          }
          files.forEach((file, index) => {
            draft.push({status: "initializing", temp_id: randomIds[index], src: srcs[index]});
          });
          return draft;
        });
        files.forEach((file, index) => {
          initializeFileUpload(file, randomIds[index]);
        });
      }).catch(function(error) {
        console.error(error);
        setError("Something went wrong uploading that file.");
      });
    }
    fileInputRef.current.value = null;
  }

  const initializeFileUpload = (file, temp_id) => {
    let filename = file.name;
    let size = file.size;
    let mimetype = file.type;

    if (onUploadStart) {
      onUploadStart(file);
    }

    publicAction("createFile", { type, mimetype, size, filename }).then(function({ data }) {
      let file_id = data.file_id;
      let upload_filename = data.filename;

      updateValue(draft => {
        let index = draft.findIndex(f => f.temp_id == temp_id);
        if (index > -1) {
          draft[index].status = "uploading";
          draft[index].file_id = file_id;
        }
        return draft;
      });

      const storage = getStorage(undefined, "gs://sapientclinician");
      const fileRef = storageRef(storage, "files/"+file_id+"/"+upload_filename);

      const metadata = {
        contentType: mimetype,
        customMetadata: {
          source: "user"
        }
      };

      const uploadTask = uploadBytesResumable(fileRef, file, metadata);

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes);
          updateValue(draft => {
            let index = draft.findIndex(f => f.file_id === file_id);
            if (index > -1) {
              draft[index].progress = progress;
            }
            return draft;
          });
        }, 
        (error) => {
          console.error(error);
          updateValue(draft => {
            let index = draft.findIndex(f => f.file_id === file_id);
            if (index > -1) {
              draft.splice(index, 1);
            }
            return draft;
          });
          setError("Something went wrong uploading that file.");
          if (onUploadError) {
            onUploadError(error);
          }
        }, 
        () => {          
          let { off } = databaseSync({ query: {collection: "files", file_id: file_id}, single_document_mode: true, onChanged: (value) => {
            
            if (!value.expires) {
              updateValue(draft => {
                let index = draft.findIndex(f => f.file_id === file_id);
                if (index > -1) {
                  draft[index].status = "done";
                }
                return draft;
              });
              off();
              syncOffs.current = syncOffs.current.filter(off => off !== off);

              if (onUploadComplete) {
                onUploadComplete(value);
              }

              updateError(draft => {
                if (draft == please_wait_message) {
                  return null;
                }
                return draft;
              })
            } else if (value.error) {
              updateValue(draft => {
                let index = draft.findIndex(f => f.file_id === file_id);
                if (index > -1) {
                  draft.splice(index, 1);
                }
                return draft;
              });
              off();
              syncOffs.current = syncOffs.current.filter(off => off !== off);
              setError("Something went wrong uploading that file.");
              if (onUploadError) {
                onUploadError(value.error);
              }
            }

          }});

          syncOffs.current.push(off);

          updateValue(draft => {
            let index = draft.findIndex(f => f.file_id === file_id);
            if (index > -1) {
              draft[index].status = "processing";
            }
            return draft;
          });
        }
      );

    }).catch(function(error) {
      console.error(error);
      updateValue(draft => {
        let index = draft.findIndex(f => f.temp_id === temp_id);
        if (index > -1) {
          draft.splice(index, 1);
        }
        return draft;
      });
      setError("Something went wrong uploading that file.");
      if (onUploadError) {
        onUploadError(error);
      }
    });
  }

  const handleRemoveFile = (index) => {
    updateValue(draft => {
      draft.splice(index, 1);
      return draft;
    });
  }
  
  const handleDragStart = (event) => {
    const { active } = event;
    
    const activeFile = value.find(file => (file.temp_id || file.file_id) === active.id);
    setActiveFile(activeFile);
  };
  
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      updateValue(draft => {
        const oldIndex = draft.findIndex(item => (item.temp_id || item.file_id) === active.id);
        const newIndex = draft.findIndex(item => (item.temp_id || item.file_id) === over.id);
        
        return arrayMove(draft, oldIndex, newIndex);
      });
    }

    setActiveFile(null);
  };

  useEffect(() => {
    return () => {
      syncOffs.current.forEach(off => off());
    }
  }, []);

  const button = (typeof children === "function" ? children({ value, is_uploading, disabled, obviated, handleChooseFile }) : (
    (!multiple && value.length > 0) ? null : (
      <Button text={(value.length > 0 && multiple) ? `Add more ${type}s` : `Add ${type}`} className={small ? "small" : ""} icon={{"image": "image-outline"}[type]} disabled={disabled} obviated={obviated} onClick={handleChooseFile}/>
    )
  ))

  return (
    <>
      {title ? <label htmlFor={name}>{title}</label> : null}
      {gallery ? (
        <div className="file_picker_gallery_container" name={name}>
          {value.length > 0 ? (
            <DndContext 
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToHorizontalAxis]}
              autoScroll={{
                threshold: { x: 0.1, y: 0 }
              }}
            >
              <SortableContext 
                items={value.map(file => file.temp_id || file.file_id)}
                strategy={horizontalListSortingStrategy}
              >
                <div className="file_picker_gallery">
                  {value.map((file, index) => (
                    <SortableItem 
                      key={file.temp_id || file.file_id}
                      file={file}
                      index={index}
                      handleRemoveFile={handleRemoveFile}
                    />
                  ))}
                </div>
              </SortableContext>
              
              <DragOverlay zIndex={100}>
                {activeFile ? (
                  <GalleryItemPreview file={activeFile} />
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : null}
          <div className="buttons">
            {button}
          </div>
        </div>
      ) : button}
      <div style={{display: "none"}}>
        <input type="file" accept={{"image": "image/*", "rubric": "application/pdf"}[type]} multiple={multiple} onChange={handleFileChange} ref={fileInputRef}/>
      </div>
      {error ? <div className="input_error">{error}</div> : null}
    </>
  )
}

function GalleryItemPreview({ file, handleRemoveClick }) {
  return (
    <div className="file_picker_gallery_item drag_overlay">
      <GalleryItemInner file={file} handleRemoveClick={handleRemoveClick}/>
    </div>
  );
}

function SortableItem({ file, index, handleRemoveFile }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: file.temp_id || file.file_id
  });
  
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const [openImageModal] = useModal(({ closeModal }) => {
    return (
      <Modal image={true}>
        {file.file ? <FileImage file={file.file} onClick={closeModal}/> : <img src={file.src} onClick={closeModal}/>}
      </Modal>
    )
  });

  
  const handleItemClick = (e) => {
    if (!e.target.closest('.remove_button')) {
      openImageModal();
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.target.closest('.remove_button') && (e.key === 'Enter' || e.key === ' ')) {
      handleRemoveFile(index)
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!isDragging) {
        openImageModal();
      } else {
        listeners.onKeyDown(e);
      }
    } else if (e.key === ' ') {
      listeners.onKeyDown(e);
    }
  };
  
  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`file_picker_gallery_item`}
      onClick={handleItemClick}
      {...attributes} 
      {...listeners}
      onKeyDown={handleKeyDown}
    >
      <GalleryItemInner file={file} handleRemoveClick={() => {handleRemoveFile(index)}}/>
    </div>
  );
}

function GalleryItemInner({ file, handleRemoveClick }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  return (
    <>
      {file.file ? <FileImage file={file.file} draggable="false" className="gallery_item_image" onLoad={() => setImageLoaded(true)} onError={() => setLoadError(true)}/> : <img src={file.src} draggable="false" className={"gallery_item_image " + (file.status !== "done" ? "blurred" : "")}/>}
      <div 
        className="remove_button" 
        onClick={(e) => {
          e.stopPropagation();
          handleRemoveClick();
        }} 
        tabIndex={0}
        aria-label="Remove file"
      >
        <Icon name="close"></Icon>
      </div>
      {((!file.file && file.status !== "done") || (file.file && !imageLoaded && !loadError)) ? <div className="gallery_item_spinner"><svg className={"spinner" + ((file.status == "uploading" || file.status == "initializing") ? " frozen" : "")} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><circle className="path" fill="none" strokeWidth="10" strokeLinecap="butt" cx="50" cy="50" r="40" strokeDashoffset={(file.status == "uploading" || file.status == "initializing") ? (265 - ((file.progress || 0) * 251)) : undefined}></circle></svg></div> : null}
      {loadError ? <div className="gallery_item_error"><Icon name="alert-circle-outline"/></div> : null}
    </>
  )
}